import React from 'react';

import { Checkbox, CustomTooltip, DynamicIconWrapper, Icons } from '@components/common';

import { useMutateKaiAssistants } from '@hooks/kaiAssistants/useMutateKaiAssistants';

import { IKaiAssistantDTO } from '@interfaces/kai/dto/kaiDTO';

import UsersTooltip from '../../../common/UsersTooltip';

import styles from './KaiAssistantsList.module.scss';

interface KaiAssistantsListProps {
	kaiAssistants: IKaiAssistantDTO[];
	openDeleteConfirmationModal: (assistantId: string) => () => void;
	openEditModal: (assistant: IKaiAssistantDTO) => () => void;
	isEnabled: boolean;
}

const KaiAssistantsList = ({
	kaiAssistants,
	openDeleteConfirmationModal,
	openEditModal,
	isEnabled,
}: KaiAssistantsListProps) => {
	const {
		enableKaiAssistant,
		disableKaiAssistant,
		setDefaultKaiAssistant,
		setNonDefaultKaiAssistant,
	} = useMutateKaiAssistants();

	const onChangeCheckbox = (assistantId: string) => async (value: boolean) => {
		if (value) {
			await enableKaiAssistant(assistantId);
		} else {
			await disableKaiAssistant(assistantId);
		}
	};

	const changeDefaultAssistant = (isDefault: boolean, assistantId: string) => async () => {
		if (!isDefault) {
			await setDefaultKaiAssistant(assistantId);
		} else {
			await setNonDefaultKaiAssistant(assistantId);
		}
	};

	return (
		<div className={styles.kaiAssistantsCard}>
			{kaiAssistants.map(kaiAssistant => {
				const favoriteIconTooltipText = kaiAssistant.isDefault
					? 'Revoke default'
					: 'Set as default';

				return (
					<div
						key={kaiAssistant.id}
						className={styles.kaiAssistantsCard__item}
					>
						<div className={styles.checkboxContainer}>
							<Checkbox
								key={kaiAssistant.id}
								checked={kaiAssistant.isEnabled}
								disabled={!isEnabled}
								disabledTooltip='Enable Kai Assistant on the platform'
								isFilledStyle
								name={kaiAssistant.id}
								onChange={onChangeCheckbox(kaiAssistant.id)}
							/>
							<div className={styles.checkboxLabel}>
								{kaiAssistant.name}
							</div>
						</div>
						<div
							className={styles.kaiAssistantsCard__item__actions}
						>
							<CustomTooltip
								interactive
								isDisabled={!kaiAssistant.users.length}
								placement='top'
								tooltip={(
									<UsersTooltip
										headerText='This Kai Assistant is shown to:'
										users={kaiAssistant.users}
									/>
								)}
							>
								<div className={styles.userCounter}>
									{`${kaiAssistant.users.length}`} users
								</div>
							</CustomTooltip>
							<DynamicIconWrapper
								icon={Icons.favoriteIcon}
								isDirty={!!kaiAssistant.isDefault}
								onClick={changeDefaultAssistant(!!kaiAssistant.isDefault, kaiAssistant.id)}
								tooltipLabel={favoriteIconTooltipText}
							/>
							<DynamicIconWrapper
								icon={Icons.editIcon}
								onClick={openEditModal(kaiAssistant)}
							/>
							<DynamicIconWrapper
								icon={Icons.delete}
								onClick={openDeleteConfirmationModal(kaiAssistant.id)}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default KaiAssistantsList;
